import { Engine } from 'engine/Engine';
import { EngineContext } from 'engine/components/EngineProvider';
import { SvgRenderer } from 'engine/components/SvgRenderer';
import { UIRenderer } from 'engine/components/UIRenderer';
import { V2, emptyV2 } from 'engine/lib/V2';
import { create } from 'engine/lib/create';
import { linkStyles } from 'game/components/Link';
import { FilterGlow } from 'game/defs/FilterGlow';
import { FilterGlowBeige } from 'game/defs/FilterGlowBeige';
import { FilterGlowSubtle } from 'game/defs/FilterGlowSubtle';
import { FilterInnerDropShadow } from 'game/defs/FilterInnerDropShadow';
import { FilterOutline } from 'game/defs/FilterOutline';
import { PatternFence, PatternFenceTop } from 'game/defs/PatternFence';
import { PatternLines } from 'game/defs/PatternLines';
import { PatternRoad } from 'game/defs/PatternRoad';
import { GameManager } from 'game/entities/GameManager';
import { createGlobalStyle } from 'game/lib/createStyles';
import { usePersistentStorage } from 'game/lib/persistentStorage';
import { DebugMenu } from 'game/tutorial/DebugMenu';
import { prefsStorage } from 'game/tutorial/prefs';
import { useAnimationFrame } from 'hooks/useAnimationFrame';
import throttle from 'lodash/throttle';
import React, { useCallback, useEffect, useRef, useState } from 'react';

createGlobalStyle('a', linkStyles);

const getViewportSize = (): V2 => [
  Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0),
  Math.max(document.documentElement.clientHeight || 0, window.innerHeight || 0),
];

const engine = new Engine();
(window as any).engine = engine;

export const App: React.FC = () => {
  const [[w, h], setSize] = useState<V2>(emptyV2);
  useEffect(() => {
    const onResize = throttle(() => {
      const newSize = getViewportSize();
      engine.viewport = newSize;
      setSize(newSize);

      // fn+F triggers a keydown event for KeyF but not a keyup event so the browser makes it seem
      // like the user is holding down F forever (unless it's pressed again without fn).
      // There's no way to know if the fn key was pressed when the F key was so instead,
      // listen for a resize (fn+F enters a fullscreen mode) and clear the pressed object.
      engine.input.pressed = {};
    }, 200);
    onResize();
    const onBlur = () => {
      engine.input.pressed = {};
    };
    window.addEventListener('resize', onResize);
    window.addEventListener('blur', onBlur);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('blur', onBlur);
    };
  }, []);

  const [menuEnabled, setMenuEnabled] = useState(false);
  useEffect(() => {
    engine.input.enable();
    engine.add(create(GameManager, { onStart: () => setMenuEnabled(true) }));
    return () => {
      engine.input.disable();
      engine.entities.forEach(e => engine.destroy(e));
    };
  }, []);

  const svgRenderer = useRef<SvgRenderer>(null);
  const uiRenderer = useRef<UIRenderer>(null);
  useAnimationFrame(useCallback(delta => {
    engine.step(delta);
    svgRenderer.current?.forceUpdate();
    uiRenderer.current?.forceUpdate();
  }, []));
  const [{ showColliders }] = usePersistentStorage(prefsStorage);

  return (
    <div className="game">
      <EngineContext.Provider value={engine}>
        <svg className="game--svg" viewBox={`0 0 ${w} ${h}`} width={w} height={h}>
          <defs>
            <FilterOutline />
            <FilterGlow />
            <FilterGlowBeige />
            <FilterGlowSubtle />
            <FilterInnerDropShadow />
            <PatternLines />
            <PatternRoad />
            <PatternFence />
            <PatternFenceTop />
          </defs>
          <SvgRenderer
            ref={svgRenderer}
            engine={engine}
            showColliders={showColliders}
          />
        </svg>
        <div className="game-ui">
          <UIRenderer ref={uiRenderer} engine={engine} />
        </div>
        {menuEnabled && (
          <DebugMenu />
        )}
      </EngineContext.Provider>
    </div>
  );
};
